import { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useHistory } from 'react-router-dom';

/**
 * Custom hook for managing URL search parameters with React Router
 * Supports scoped parameters for multi-table pages while maintaining backward compatibility
 * 
 * @param {string|null} scope - Parameter scope ('sub' for sub-tables, null for main table)
 * @returns {Object} Object containing:
 *   - searchParams: URLSearchParams instance with current parameters
 *   - updateSearchParams: Function to update multiple parameters (scoped)
 *   - getParam: Function to get a single parameter value (scoped)
 *   - clearParams: Function to clear parameters (scoped or all)
 *   - hasParam: Function to check if a parameter exists (scoped)
 *   - setParam: Function to set a single parameter (scoped)
 *   - removeParam: Function to remove a single parameter (scoped)
 *   - getScopedParams: Function to get all parameters for current scope
 *   - clearScopedParams: Function to clear all parameters for current scope
 */
export function useSearchParams(scope = null) {
  const location = useLocation();
  const history = useHistory();
  
  const [searchParams, setSearchParams] = useState(() => 
    new URLSearchParams(location.search)
  );
  
  // Update internal state when location changes (e.g., browser back/forward)
  useEffect(() => {
    setSearchParams(new URLSearchParams(location.search));
  }, [location.search]);

  // Helper to create scoped parameter names
  const getScopedKey = useCallback((key) => {
    // Main table (scope = null) uses unprefixed parameters for backward compatibility
    // Sub-tables use prefixed parameters (e.g., 'sub_search')
    return scope ? `${scope}_${key}` : key;
  }, [scope]);
  
  // Helper to extract original key from scoped key
  const getOriginalKey = useCallback((scopedKey) => {
    if (!scope) return scopedKey;
    return scopedKey.startsWith(`${scope}_`) ? 
      scopedKey.substring(scope.length + 1) : scopedKey;
  }, [scope]);

  // Check if we need to apply optimizations (debouncing, batching)
  const shouldOptimize = useMemo(() => {
    // Apply optimizations for sub-tables or when URL gets complex
    return scope === 'sub' || searchParams.toString().length > 200;
  }, [scope, searchParams]);
  
  /**
   * Update multiple search parameters at once (with scoping support)
   * @param {Object} updates - Object with key-value pairs to update
   *                          Use null/undefined/empty string to remove a parameter
   */
  const updateSearchParams = useCallback((updates) => {
    const newParams = new URLSearchParams(searchParams);
    
    // Handle parameter conflicts and optimization
    const optimizedUpdates = { ...updates };
    
    // Check for actual changes to avoid unnecessary updates
    const hasActualChanges = Object.entries(optimizedUpdates).some(([key, value]) => {
      const scopedKey = getScopedKey(key);
      const currentValue = newParams.get(scopedKey);
      const newValue = (value === null || value === undefined || value === '') ? null : String(value);
      return currentValue !== newValue;
    });
    
    if (!hasActualChanges) {
      return; // No actual changes, skip update
    }
    
    Object.entries(optimizedUpdates).forEach(([key, value]) => {
      const scopedKey = getScopedKey(key);
      if (value === null || value === undefined || value === '') {
        newParams.delete(scopedKey);
      } else {
        newParams.set(scopedKey, String(value));
      }
    });
    
    // URL length check for safety
    const newSearch = newParams.toString();
    const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ''}`;
    
    if (newUrl.length > 2000) {
      console.warn('URL length exceeds recommended limit, some parameters may not work correctly');
    }
    
    // Use replace to avoid cluttering browser history with every keystroke
    history.replace(newUrl);
    setSearchParams(newParams);
  }, [searchParams, location.pathname, history, getScopedKey]);
  
  /**
   * Get a single parameter value (scoped)
   * @param {string} key - Parameter name (will be scoped automatically)
   * @returns {string|null} Parameter value or null if not found
   */
  const getParam = useCallback((key) => {
    const scopedKey = getScopedKey(key);
    return searchParams.get(scopedKey);
  }, [searchParams, getScopedKey]);
  
  /**
   * Check if a parameter exists (scoped)
   * @param {string} key - Parameter name (will be scoped automatically)
   * @returns {boolean} True if parameter exists
   */
  const hasParam = useCallback((key) => {
    const scopedKey = getScopedKey(key);
    return searchParams.has(scopedKey);
  }, [searchParams, getScopedKey]);
  
  /**
   * Get all parameters for the current scope
   * @returns {Object} Object with key-value pairs for current scope
   */
  const getScopedParams = useCallback(() => {
    const result = {};
    searchParams.forEach((value, key) => {
      if (scope) {
        // For scoped calls, only return parameters with the correct prefix
        if (key.startsWith(`${scope}_`)) {
          const originalKey = getOriginalKey(key);
          result[originalKey] = value;
        }
      } else {
        // For main table (unscoped), exclude parameters that belong to sub-tables
        if (!key.includes('_') || !['sub'].some(s => key.startsWith(`${s}_`))) {
          result[key] = value;
        }
      }
    });
    return result;
  }, [searchParams, scope, getOriginalKey]);
  
  /**
   * Clear all search parameters (scoped or global)
   * @param {boolean} clearAll - If true, clear all parameters; if false, clear only scoped
   */
  const clearParams = useCallback((clearAll = false) => {
    if (clearAll || !scope) {
      // Clear everything
      history.replace(location.pathname);
      setSearchParams(new URLSearchParams());
    } else {
      // Clear only scoped parameters
      const updates = {};
      Object.keys(getScopedParams()).forEach(key => {
        updates[key] = null;
      });
      updateSearchParams(updates);
    }
  }, [history, location.pathname, scope, getScopedParams, updateSearchParams]);
  
  /**
   * Clear all parameters for the current scope
   */
  const clearScopedParams = useCallback(() => {
    clearParams(false);
  }, [clearParams]);
  
  /**
   * Set a single parameter (scoped)
   * @param {string} key - Parameter name (will be scoped automatically)
   * @param {string} value - Parameter value
   */
  const setParam = useCallback((key, value) => {
    updateSearchParams({ [key]: value });
  }, [updateSearchParams]);
  
  /**
   * Remove a single parameter (scoped)
   * @param {string} key - Parameter name (will be scoped automatically)
   */
  const removeParam = useCallback((key) => {
    updateSearchParams({ [key]: null });
  }, [updateSearchParams]);
  
  return { 
    searchParams, 
    updateSearchParams, 
    getParam, 
    hasParam,
    clearParams,
    setParam,
    removeParam,
    getScopedParams,
    clearScopedParams,
    scope // Expose scope for debugging/external use
  };
}

/**
 * Utility function to parse pagination parameters from URL (with scope support)
 * @param {URLSearchParams} searchParams - Search parameters instance
 * @param {string|null} scope - Parameter scope ('sub' for sub-tables, null for main table)
 * @returns {Object} Pagination model with page and pageSize
 */
export function parsePaginationFromUrl(searchParams, scope = null) {
  const pageKey = scope ? `${scope}_page` : 'page';
  const pageSizeKey = scope ? `${scope}_page_size` : 'page_size';
  
  return {
    page: parseInt(searchParams.get(pageKey) || '0', 10),
    pageSize: parseInt(searchParams.get(pageSizeKey) || '50', 10)
  };
}

/**
 * Utility function to parse sort parameters from URL (with scope support)
 * @param {URLSearchParams} searchParams - Search parameters instance
 * @param {string|null} scope - Parameter scope ('sub' for sub-tables, null for main table)
 * @returns {Array} Sort model array
 */
export function parseSortFromUrl(searchParams, scope = null) {
  const sortByKey = scope ? `${scope}_sort_by` : 'sort_by';
  const sortOrderKey = scope ? `${scope}_sort_order` : 'sort_order';
  
  const sortBy = searchParams.get(sortByKey);
  const sortOrder = searchParams.get(sortOrderKey);
  
  if (sortBy) {
    return [{ field: sortBy, sort: sortOrder || 'asc' }];
  }
  
  return [];
}

/**
 * Utility function to parse filter parameters from URL (with scope support)
 * @param {URLSearchParams} searchParams - Search parameters instance
 * @param {string|null} scope - Parameter scope ('sub' for sub-tables, null for main table)
 * @returns {string} Filter text value
 */
export function parseFilterFromUrl(searchParams, scope = null) {
  const searchKey = scope ? `${scope}_search` : 'search';
  return searchParams.get(searchKey) || '';
}

/**
 * Utility hook for managing selection state in URLs
 * @param {string} entityType - Type of entity being selected (e.g., 'vendor', 'item')
 * @param {string} rowKey - Key used for row identification
 * @param {Array} data - Current table data for validation
 * @param {boolean} enabled - Whether selection management is enabled (default: true)
 * @returns {Object} Selection state management functions
 */
export function useSelectionState(entityType, rowKey, data = [], enabled = true) {
  const { getParam, setParam } = useSearchParams(); // Use global scope for selection
  const [selectedRow, setSelectedRowState] = useState(null);
  
  // Always create selectionKey, even if entityType is null
  const selectionKey = entityType ? `selected_${entityType}` : null;
  const isEnabled = !!(entityType && rowKey && enabled);
  
  // Restore selection from URL when data loads
  useEffect(() => {
    if (!isEnabled || !selectionKey) {
      setSelectedRowState(null);
      return;
    }
    
    const selectedId = getParam(selectionKey);
    console.log(`[useSelectionState] Effect running - selectedId: ${selectedId}, dataLength: ${data?.length || 0}, rowKey: ${rowKey}`);
    
    if (!selectedId) {
      // No selection in URL, clear state
      setSelectedRowState(null);
      return;
    }
    
    if (!data || data.length === 0) {
      // Data not loaded yet, keep waiting but don't clear selection
      console.log(`[useSelectionState] Data not loaded yet, keeping selection: ${selectedId}`);
      return;
    }
    
    // Data is loaded, try to find the selected item
    const found = data.find(row => row[rowKey] === selectedId);
    if (found) {
      console.log(`[useSelectionState] Found selected item:`, found);
      setSelectedRowState(found);
    } else {
      // Item not found in current data - could be on different page or invalid
      console.warn(`[useSelectionState] Selected item ${selectedId} not found in current data. Available items:`, data.map(row => row[rowKey]));
      // Don't immediately clear - the item might be on a different page
      // Only clear if we're confident it's invalid
    }
  }, [data, rowKey, getParam, setParam, selectionKey, isEnabled]);
  
  // Update URL when selection changes
  const setSelectedRow = useCallback((row) => {
    if (!isEnabled || !selectionKey) return;
    
    setSelectedRowState(row);
    if (row && row[rowKey]) {
      setParam(selectionKey, row[rowKey]);
    } else {
      setParam(selectionKey, null);
    }
  }, [rowKey, setParam, selectionKey, isEnabled]);
  
  // Clear selection
  const clearSelection = useCallback(() => {
    if (!isEnabled) return;
    setSelectedRow(null);
  }, [setSelectedRow, isEnabled]);
  
  // Get current selection ID from URL
  const getSelectedId = useCallback(() => {
    if (!isEnabled || !selectionKey) return null;
    return getParam(selectionKey);
  }, [getParam, selectionKey, isEnabled]);
  
  // Get the selected ID from URL (might exist even if selectedRow is null)
  const selectedIdFromUrl = isEnabled && selectionKey ? getParam(selectionKey) : null;
  
  return {
    selectedRow: isEnabled ? selectedRow : null,
    setSelectedRow,
    clearSelection,
    getSelectedId,
    hasSelection: isEnabled && (!!selectedRow || !!selectedIdFromUrl),
    selectedId: selectedIdFromUrl // Expose the ID even when row not found yet
  };
}
